"use client";

import { Check, Globe } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DEFAULT_LOCALE,
  LOCALES,
  buildLocaleCookie,
  isLocale,
  localeShortLabel,
  type Locale,
} from "@/i18n/config";
import { NAV_LANGUAGE_TRIGGER_CLASS } from "@/lib/text-reflow";

interface LanguageSwitcherProps {
  variant?: "nav" | "sidebar" | "footer";
}

export function persistLocalePreference(locale: Locale) {
  document.cookie = buildLocaleCookie(locale);
}

export function LanguageSwitcher({ variant = "nav" }: LanguageSwitcherProps) {
  const locale = useLocale();
  const router = useRouter();
  const t = useTranslations("LanguageSwitcher");
  const currentLocale = isLocale(locale) ? locale : DEFAULT_LOCALE;

  function switchLocale(nextLocale: Locale) {
    persistLocalePreference(nextLocale);
    router.refresh();
  }

  const triggerClassName =
    variant === "sidebar"
      ? "inline-flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-1.5 text-xs font-medium text-(--mk-cream-60) transition-colors hover:bg-(--mk-cream-surface) hover:text-(--mk-cream)"
      : variant === "footer"
        ? "inline-flex cursor-pointer items-center gap-2 text-sm text-(--mk-cream-75) transition-colors hover:text-(--mk-cream)"
        : NAV_LANGUAGE_TRIGGER_CLASS;

  const globeClassName =
    variant === "sidebar"
      ? "h-3.5 w-3.5 shrink-0"
      : variant === "footer"
        ? "h-4 w-4 shrink-0"
        : "h-5 w-5 shrink-0";

  const optionLabels: Record<Locale, string> = {
    en: t("en"),
    es: t("es"),
    pt: t("pt"),
    de: t("de"),
  };

  return (
    <DropdownMenu modal={variant === "nav" ? undefined : false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={triggerClassName}
          aria-label={t("label")}
        >
          <Globe className={globeClassName} aria-hidden="true" />
          {variant === "sidebar" ? (
            <span>{localeShortLabel(currentLocale)}</span>
          ) : null}
          {variant === "footer" ? (
            <span>{optionLabels[currentLocale]}</span>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={variant === "nav" ? "bottom" : "top"}
        align={variant === "footer" ? "start" : "end"}
        className="w-48 border-(--mk-cream-08) bg-(--mk-ink-soft) text-(--mk-cream)"
      >
        {LOCALES.map((option) => (
          <DropdownMenuItem
            key={option}
            onClick={() => switchLocale(option)}
            className="cursor-pointer text-(--mk-cream-dim) hover:text-(--mk-cream)"
          >
            <span className="flex-1">{optionLabels[option]}</span>
            {currentLocale === option ? (
              <Check className="h-4 w-4 text-(--mk-gold)" />
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
